/**
 * MONSTER TOWN — SIGNPOSTS. HUD/AUDIO/TOUCH CREW (src/ui/**).
 *
 * ============ WHY GUIDANCE CANNOT BE MADE OF HINTS ALONE ============
 * The nudge system is deliberately, correctly stingy: 2-3 shows per rule FOR
 * LIFE, a 30 s cooldown that any interaction restarts, and a 14-20 s idle gate.
 * Measured, that means roughly five hints across the entire life of a save, and
 * on a second identical run nothing appeared inside 26 s at all. That restraint
 * is right — a game that nags is a game a parent turns off.
 *
 * But it left the world with NOTHING ELSE. No arrow, no sparkle, no pointing
 * neighbour aimed at the restaurant door, the job board, or the kid's own house
 * — which is one of four near-identical buildings with no mark saying it is
 * theirs. A kid who stalls in the town on day three had nothing to follow, and
 * the one HUD button wearing a house opened the save-slot picker instead.
 *
 * SIGNPOSTS ARE THE PART THAT NEVER RUNS OUT. They are not hints and they are
 * not a tutorial: they are labels on the doors, the way a real town has signs.
 * They are always there, they never nag, they never expire, and — LAW #1 —
 * tapping one does something: your monster walks there and goes in.
 *
 * WHAT GETS ONE, AND WHY IT IS DATA:
 *   1. every PORTAL in the scene you are standing in — the engine already knows
 *      where the doors are, so the signs can never disagree with the world; and
 *   2. any placement whose id matches `LANDMARKS` (the job board, the shop
 *      front), for the things that are destinations without being doors.
 * A new venue drops in with a portal and gets a sign for free. No engine edit,
 * no list to maintain, nothing to forget.
 *
 * THE HOME SIGN IS THE KID'S OWN MONSTER COLOUR, so "which of these four houses
 * is mine" has an answer that does not require reading, counting or memory.
 *
 * HARD RULES HONOURED HERE:
 *  - Positions come from `worldToCanvasPx()`, i.e. the CANVAS bounding rect.
 *    Never window.innerWidth/innerHeight (contract §12).
 *  - 88 px targets, one tap, no double-tap, nothing to dismiss.
 *  - Play mode only, exterior only, and gone entirely in photo mode and behind
 *    panels — a sign must never be in the picture a kid shows a grown-up.
 *  - Cheap on the hand-me-down: at most `MAX_SIGNS` chips, rebuilt only when the
 *    scene changes, and re-projected every third frame rather than every frame.
 */

import * as THREE from 'three'
import { bus } from '../core/events'
import { engine } from '../core/engine'
import { world } from '../core/scene'
import { getPlaceable } from '../core/placeable'
import type { Cell } from '../core/grid'
import { css, type PaletteName } from '../core/palette'
import type { Vec3 } from '../core/types'
import { worldToCanvasPx } from '../input/pick'
import { app, type GameContext } from '../app/context'
import { el, onTap } from './dom'
import { iconEl } from './icons'

/** More than this on screen at once is clutter, not guidance. */
const MAX_SIGNS = 6
/** Re-project this often. 4 = about eight times a second at the 30 Hz sim. */
const EVERY = 4
/**
 * ============ A SIGN STANDS ON THE THING, NOT A CONSTANT ABOVE IT ============
 *
 * WAS 104 px above a GROUND point, and before that 74. Both numbers were wrong
 * for the same reason and no third number would have been right: the anchor a
 * sign is given is the doorstep, and the lift therefore has to clear a building
 * whose height the HUD has never been told. At 74 the ice cream shop's marker
 * covered the shop; at 104 the house sign's post *"enters the red roof"* (vr1,
 * photographed at noon and again at ten at night), and in the zoo the way OUT
 * hovered a metre above and to the left of its own doorway.
 *
 * Both anchors now come from the TOP of the thing:
 *   - in the town, `world:landmarks` carries `top`, the world Y of the highest
 *     point of that landmark's art (see the note in `src/world/hotspots.ts`);
 *   - indoors, the door's own art bounding box, and its FOOTPRINT CENTRE rather
 *     than its anchor cell — a two-tile door anchored at one corner is why the
 *     zoo's exit sign was off to the left as well as too high.
 *
 * What is left is the length of the sign's own post: `.mt-sign-card::after` is
 * 18 px of pole below an 88 px button, so this is how far the card floats above
 * the point its post is standing on. It is a drawing measurement now, not a
 * guess about architecture, and it is the same at every zoom because the ANCHOR
 * moved with the camera instead of the offset.
 */
const LIFT_PX = 24

/**
 * HOW BIG THE CARD IS AT EACH ZOOM STEP, in CSS px. Index matches
 * `camera.zoomLevel` (0 = closest, 3 = widest).
 *
 * THE MARKERS DID NOT SCALE (fixed r3). They were a flat 88px card at every
 * zoom, so zooming OUT — the thing a kid does to see their whole town — made
 * them take a bigger and bigger share of the picture until four white cards
 * dominated the composition. In the wide shot the town was the background and
 * the UI was the subject. They now shrink as the world does.
 *
 * THE TOUCH TARGET DOES NOT SHRINK WITH THEM: the button stays a full 88px
 * (`--touch-min`) and only the drawn card inside it changes size, so a fat
 * finger has exactly as much to aim at as before at every zoom.
 */
const SIGN_SIZE: readonly number[] = [78, 70, 62, 54]

/**
 * PICTURES A SIGN IS ALLOWED TO WEAR. Remap only — the world crew's own icon
 * vocabulary is unchanged, because the same names mean something specific in
 * the venue's order tickets ('cone' is the CONE step of an ice cream, not the
 * shop). At sign size those drawings failed: 'cone' is a tan triangle with no
 * scoop on it (the critic read it as "a dark triangle") and 'plate' is a white
 * ellipse on a white card ("a bare white ellipse outline"). These two say
 * ICE CREAM SHOP and PLACE TO EAT at a glance, in colour, filled to the edges.
 * DATA — a new venue adds a line.
 */
const SIGN_ICON: Readonly<Record<string, string>> = {
  cone: 'iceCream',
  portion: 'iceCream',
  plate: 'dinner',
  cup: 'iceCream',
  coin: 'bag',
}

/**
 * AND WHAT COLOUR THE CARD IS. Four identical white cards read as four bits of
 * UI dropped on the town; four different coloured signs read as four different
 * places. The tint is a pastel from the palette so the picture on top of it
 * keeps its contrast. DATA.
 */
const SIGN_TINT: Readonly<Record<string, PaletteName>> = {
  iceCream: 'wallPink',
  dinner: 'wallMint',
  bag: 'wallBlue',
  star: 'wallCream',
  house: 'wallCream',
  door: 'wallLilac',
}

/**
 * WHAT PICTURE A DESTINATION GETS. First match on the destination scene id.
 * DATA — a new venue adds a line, never a change below.
 */
const PLACE_ICON: readonly (readonly [string, string])[] = [
  ['house', 'house'],
  ['home', 'house'],
  ['icecream', 'cone'],
  ['ice', 'cone'],
  ['restaurant', 'plate'],
  ['venue', 'cone'],
  ['shop', 'bag'],
]

/** Placed things worth pointing at that are not doors. DATA, on the def id. */
const LANDMARKS: readonly (readonly [string, string])[] = [
  ['town_job_board', 'star'],
  ['shop_front', 'bag'],
  ['venue_shopfront', 'cone'],
]

/**
 * WHICH DESTINATION IS "HOME". `app.homeSceneId` is the TOWN — the outdoor
 * scene the kid lives in — so it cannot answer "which of these four buildings
 * is my house". The house is an interior, and its scene id says so.
 */
function isHome(sceneId: string): boolean {
  const low = sceneId.toLowerCase()
  return low.startsWith('house') || low.startsWith('home')
}

function iconFor(sceneId: string): string {
  const low = sceneId.toLowerCase()
  for (const [frag, icon] of PLACE_ICON) {
    if (low.includes(frag)) return icon
  }
  return 'door'
}

interface Sign {
  node: HTMLElement
  at: Vec3
  /**
   * The placed instance this sign is a label FOR, when it has one. A sign is
   * allowed to sit on its own subject — that is what a label does — and on
   * nothing else. See `hotspots` below.
   */
  owner: string | null
}

/**
 * ==========================================================================
 *  A SIGN MAY NOT SIT ON A THING A KID CAN TAP (fixed vr2 — BLOCKER)
 * ==========================================================================
 * This file already had one absolute rule — *"a sign may never sit on a
 * control"* — because a target that measures fine and hit-tests to something
 * else is the OCEAN RUSH bug. It was only ever enforced against the HUD, and
 * the world is full of controls too.
 *
 * MEASURED, in the zoo, on `ipad-old` (`tools/fix-int-r2-probe.mjs --which
 * trough`): the "Go to door" sign is an 88x88 BUTTON at (374,294) and the
 * feeding trough projects to (417,373) — dead centre of it. Nine
 * `elementFromPoint` probes in a grid around the trough came back
 * `BUTTON.mt-sign` / `DIV.mt-sign-card`, and one real click at the trough's own
 * screen point emitted `play:interacted { defId: 'venue_exit' }` and put the kid
 * back in the town. That is the last step of the reference errand — the payoff
 * the whole expansion is named after — swallowed by a label for the door, and
 * it was invisible to every gate because `zoo-check` is allowed to open the
 * trough's panel by API.
 *
 * So the projection now knows where every placed thing in the scene is, and the
 * sign's own 88px box may not contain any of their tap points except its own.
 * The outward ring search that already existed for chrome does the moving.
 *
 * COST: these are WORLD points, collected once per rebuild (a placement event,
 * not a frame) and projected with plain matrix maths — no layout reads, which
 * is the expensive half of a pass. Bounded by `MAX_HOTSPOTS` so a kid who fills
 * a town with 400 flowers cannot make a signpost expensive.
 */
const MAX_HOTSPOTS = 90
/** How close a sign's box may come to another thing's tap point. */
const HOTSPOT_PAD = 6

/**
 * The world Y of the top of a piece of art, for standing a sign on it. One
 * shared box, because this runs over every door in a room on every rebuild and
 * allocating a Box3 per door on the hand-me-down is exactly the kind of thing
 * that shows up as a hitch when a kid walks through a door.
 */
const artBox = new THREE.Box3()
function topOfArt(art: THREE.Object3D, fallback: number): number {
  artBox.setFromObject(art)
  return isFinite(artBox.max.y) ? artBox.max.y : fallback
}

/** The world crew's last answer to `world:landmarksRequest`. */
let townLandmarks: {
  id: string
  at: Vec3
  icon: string
  top?: number
  toScene?: string
  panel?: string
}[] = []
bus.on('world:landmarks', (e) => {
  townLandmarks = e.list.map((l) => ({ ...l }))
})

export function installSignposts(ctx: GameContext, hud: HTMLElement): void {
  const layer = el('div', 'mt-signs')
  hud.append(layer)

  let signs: Sign[] = []
  /** Every placed thing in the awake scene, as a world point. See `Sign.owner`. */
  let hotspots: { at: Vec3; owner: string }[] = []
  let tick = 0

  /**
   * A SIGN IS A 88px TOUCH TARGET WITH A SMALLER CARD DRAWN INSIDE IT.
   *
   * The two are deliberately separate objects: the button is what a finger has
   * to hit and never changes size, the `.mt-sign-card` is what the eye sees and
   * shrinks as the camera pulls back (see SIGN_SIZE). Before this they were the
   * same box, so the only way to stop the markers dominating a wide shot would
   * have been to shrink the target — which is the one thing that is not allowed.
   */
  const chip = (icon: string, home: boolean, go: () => void): HTMLElement => {
    const node = el('button', 'mt-sign')
    node.type = 'button'
    const pic = SIGN_ICON[icon] ?? icon
    node.setAttribute('aria-label', home ? 'Go home' : `Go to ${pic}`)
    node.dataset['home'] = String(home)
    const card = el('div', 'mt-sign-card')
    if (home) {
      // The kid's own front door, in the kid's own monster's colour.
      card.style.setProperty('--sign-tint', css(app.monster.color))
    } else {
      const tint = SIGN_TINT[pic]
      if (tint !== undefined) card.style.setProperty('--sign-tint', css(tint))
    }
    // The picture is sized FROM the card, so a shrunk sign loses area and never
    // legibility. `iconEl` writes its size inline (deliberately — see the note
    // in icons.ts about a stylesheet that had not landed inflating an icon to
    // the width of the screen), so the size has to be handed back inline too.
    const ico = iconEl(pic, 44)
    ico.style.width = 'calc(var(--sign-card, 62px) - 22px)'
    ico.style.height = 'calc(var(--sign-card, 62px) - 22px)'
    card.append(ico)
    node.append(card)
    onTap(node, go)
    return node
  }

  const rebuild = (): void => {
    layer.replaceChildren()
    signs = []
    hotspots = []
    const sceneId = world.activeSceneId
    if (sceneId === null) return
    const node = world.get(sceneId)
    if (node === undefined) return

    /*
     * WHERE EVERY TAPPABLE THING IS. The same point `pickInstance` would resolve
     * a finger to — the footprint centre, lifted a little so a tall thing is
     * measured where its art is rather than at its feet. Collected here rather
     * than per projection because it only changes when the town does.
     */
    for (const p of node.grid.all()) {
      if (hotspots.length >= MAX_HOTSPOTS) break
      const c = node.grid.footprintCenter(p.anchor, p.footprint, p.rot)
      hotspots.push({ at: { x: c.x, y: c.y + 0.4, z: c.z }, owner: String(p.instanceId) })
    }

    const seenScenes = new Set<string>()
    const add = (at: Vec3, icon: string, home: boolean, owner: string | null, go: () => void): void => {
      if (signs.length >= MAX_SIGNS) return
      const el2 = chip(icon, home, go)
      layer.append(el2)
      signs.push({ node: el2, at, owner })
    }

    /**
     * INDOORS, THE ONE SIGN THAT MATTERS IS THE WAY OUT (venue fix, r3).
     *
     * Signposting used to stop dead at the front door: `kind !== 'exterior'`
     * returned before a single chip was built, so `__ui.signs()` was 0 in both
     * venues and the town shop. The town floats four chips telling a kid where
     * to go and then goes silent the moment they step inside a building, where
     * the only way back out is a mat on the floor. A three-year-old who walks
     * into the ice cream shop has no way to learn how to leave it.
     *
     * A room's exits are DATA — any placement whose def is in the `door`
     * category — so a venue added tomorrow gets its way-out sign for free, and
     * the chip runs the same auto-walk-then-interact path as tapping the mat.
     */
    if (node.def.kind !== 'exterior') {
      for (const p of node.grid.all()) {
        const def = getPlaceable(p.defId)
        if (def === undefined || def.category !== 'door') continue
        /*
         * THE MIDDLE OF THE DOOR, AND THE TOP OF IT.
         *
         * VR1, in venue #3: *"the exit-door sign is a purple lozenge hovering
         * roughly a metre above and to the LEFT of the actual doorway, floating
         * over the food trough. When the thing floating free of its anchor is
         * the way OUT, it stops being cosmetic."* Two errors, both here: the
         * anchor CELL of a multi-tile door is one of its corners, not its
         * middle, and the sign was then lifted 104 px above the floor rather
         * than standing on the door.
         *
         * The grid can answer the first (`footprintCenter` respects rotation).
         *
         * ============ AND IT STANDS ON THE DOORSTEP, NOT OVER IT (vr2) ============
         * The second half used to be the TOP of the door's art, and a venue exit
         * is a 1.5-tile gateway — so the card floated a whole doorway's height
         * up into the room, which under a fixed isometric camera means *further
         * into the room*. Photographed in venue #3: *"a door signpost floats
         * mid-air on a stubby pole in the middle of the floor beside the actual
         * door art"*, and 88px of it was lying across the feeding trough.
         *
         * Indoors the anchor is now the door's own FLOOR point. The card lands
         * on the threshold with its little post standing on the mat, which is
         * where a sign for a door belongs and is the one height that cannot
         * drift away from the thing it names as the camera turns. The town keeps
         * standing its signs on rooftops (below) — out there the thing being
         * named really is a building, and the roof is its top.
         */
        const c = node.grid.footprintCenter(p.anchor, p.footprint, p.rot)
        add({ x: c.x, y: c.y, z: c.z }, 'door', false, String(p.instanceId), () => {
          bus.emit('play:interactRequested', { instanceId: p.instanceId, sceneId })
        })
      }
      return
    }

    // Ask the world crew where its doors are. Synchronous reply, so the answer
    // is in `townLandmarks` by the time the next line runs — see ui/events.ts.
    bus.emit('world:landmarksRequest', {})

    // 1. THE TOWN'S OWN FRONT DOORS. Static art with tap hotspots — the shop,
    //    the ice cream shop, the restaurant, the kid's house. Tapping the sign
    //    runs the world crew's own door path, so a sign behaves exactly like
    //    the door it names.
    for (const l of townLandmarks) {
      const dest = String(l.toScene ?? l.panel ?? '')
      if (dest !== '') seenScenes.add(dest)
      const home = isHome(dest)
      // PREFER THE BUILDING'S OWN SIGN. The world crew already chose a picture
      // for each front door (it is what the door's toast shows), so a signpost
      // wearing the same one keeps the HUD and the world saying the same thing.
      // `door` is its "no sign" value, so fall back to the destination then.
      const own = l.icon !== '' && l.icon !== 'door' ? l.icon : iconFor(dest)
      // Stand it on the roof, not a fixed distance above the doorstep. `top` is
      // optional so a world crew that has not answered yet still gets a sign.
      add({ x: l.at.x, y: l.top ?? l.at.y, z: l.at.z }, home ? 'house' : own, home, null, () => {
        bus.emit('world:landmarkTap', { id: l.id })
      })
    }

    // 2. Doors the engine knows about as portals, for any scene the town's
    //    static landmarks did not already cover (interiors added later, other
    //    crews' venues). Deduped on destination so a door never gets two signs.
    for (const portal of world.portalsIn(sceneId)) {
      const dest = String(portal.toSceneId)
      if (seenScenes.has(dest)) continue
      seenScenes.add(dest)
      const home = isHome(dest)
      const cell: Cell = portal.cell
      // Same rule as the room doors above: stand it on the door.
      const spot = node.grid.cellToWorld(cell)
      const portalArt = node.grid.placementAt(cell)
      const portalTop =
        portalArt === null ? spot.y : topOfArt(node.art.get(portalArt.instanceId) ?? node.root, spot.y)
      add({ x: spot.x, y: portalTop, z: spot.z }, home ? 'house' : iconFor(dest), home, portalArt === null ? null : String(portalArt.instanceId), () => {
        // Exactly what tapping the door itself does — auto-walk-then-interact
        // is the app's own play path (contract §13).
        const placement = node.grid.placementAt(cell)
        if (placement !== null) {
          bus.emit('play:interactRequested', { instanceId: placement.instanceId, sceneId })
          return
        }
        bus.emit('play:walkTo', { sceneId, target: node.grid.cellToWorld(cell) })
      })
    }

    // 3. Placed destinations that are not doors at all — the job board.
    for (const p of node.grid.all()) {
      const hit = LANDMARKS.find(([frag]) => String(p.defId).includes(frag))
      if (hit === undefined) continue
      const c = node.grid.footprintCenter(p.anchor, p.footprint, p.rot)
      const art = node.art.get(p.instanceId)
      add({ x: c.x, y: art === undefined ? c.y : topOfArt(art, c.y), z: c.z }, hit[1], false, String(p.instanceId), () => {
        bus.emit('play:interactRequested', { instanceId: p.instanceId, sceneId })
      })
    }
  }

  const visible = (): boolean =>
    app.mode === 'play' &&
    hud.dataset['photo'] !== '1' &&
    app.phase !== 'monsterCreator' &&
    document.querySelector('.mt-panel') === null

  /**
   * WHERE THE CHROME IS, SO A SIGN NEVER LANDS ON IT.
   *
   * Caught in a captured frame, not in the code: with the town framed so the
   * ice cream shop and the job board sat behind the left rail, two signs
   * projected straight on top of the rail's buttons and the whole corner read
   * as mangled overlapping cards. A sign that covers a control is worse than no
   * sign — it is the OCEAN RUSH shape of bug all over again, a target that
   * measures fine and hit-tests to something else.
   *
   * Measured from each widget's OWN bounding rect, in canvas space, and cached
   * until something that can move them happens. Never `window`.
   */
  const CHROME = ['.mt-purse', '.mt-cam', '.mt-leftstack', '.mt-botright', '.mt-botleft', '#mt-build']
  /**
   * HOW FAR A SIGN HAS TO STAY FROM A CONTROL. Was 8px, and 8px is nothing.
   *
   * Measured on both iPad profiles: "Go home" sat 88x88 at (924,78) with an 8px
   * gutter to "Turn the town left" at (1020,60) — two 88px targets a fat finger
   * apart, one of which is the child's own house and the other of which spins
   * the entire world and MOVES EVERY SIGN, so a miss does not just do the wrong
   * thing, it relocates the thing they were reaching for. A finger is about
   * 44px; the gutter is now a finger, everywhere, and a sign that cannot find
   * that much room hides instead (see `stuck` below).
   */
  const GUTTER = 48
  /**
   * Sign-to-sign, and it is the same finger. Two markers 14px apart measure
   * fine and still hand a 3-year-old a coin-flip between the ice cream shop and
   * the job board. When two places are genuinely that close together on screen
   * — which only happens zoomed all the way out — one of them says nothing
   * rather than both of them saying something a finger cannot separate.
   */
  const SIGN_GUTTER = 44
  let chrome: { l: number; t: number; r: number; b: number }[] | null = null
  const remeasure = (): void => {
    chrome = null
  }
  window.addEventListener('resize', remeasure)
  window.addEventListener('orientationchange', remeasure)
  bus.on('app:modeChanged', remeasure)
  bus.on('ui:panel', remeasure)
  bus.on('build:paletteToggled', remeasure)

  const chromeBoxes = (): { l: number; t: number; r: number; b: number }[] => {
    if (chrome !== null) return chrome
    // In the LAYER'S box, because that is the box a sign's `left`/`top` resolve
    // in. See the note in `project()`.
    const base = layer.getBoundingClientRect()
    const out: { l: number; t: number; r: number; b: number }[] = []
    for (const sel of CHROME) {
      const node = hud.querySelector<HTMLElement>(sel)
      if (node === null || node.offsetParent === null) continue
      const r = node.getBoundingClientRect()
      if (r.width === 0 || r.height === 0) continue
      out.push({
        l: r.left - base.left - GUTTER,
        t: r.top - base.top - GUTTER,
        r: r.right - base.left + GUTTER,
        b: r.bottom - base.top + GUTTER,
      })
    }
    chrome = out
    return out
  }

  /** Half a sign. A sign is exactly one touch target wide. */
  const HALF = 44
  /**
   * ============ A DESTINATION SIGN IS NEVER TAKEN AWAY ============
   *
   * ROUND-3 BLOCKER: "The route into the ice cream shop is not reliably on
   * screen … 'Go to cone' was present in one framing but measured 0x0 (not
   * rendered) on two subsequent returns to town, and on the 1024x768 profile
   * there was no 'Go to cone' chip at all. So the one venue v1 is meant to ship
   * well is the one whose front door a kid cannot count on finding."
   *
   * A `MAX_DRIFT` of 200px used to hide any sign the clamp had to move further
   * than that — i.e. exactly the signs whose building is off the edge of the
   * picture, which is the one case where a kid most needs a way to get there.
   * The reasoning ("shoved that far it no longer names anything") was right
   * about a sign that PRETENDS to sit on a building. It is wrong about a sign
   * that is honestly parked on the edge of the screen pointing out of frame.
   *
   * So there is no drift limit any more. A sign whose building is off screen
   * docks against the border and says so (`data-edge`, which drops the little
   * post — it is no longer standing on anything). Tapping it still walks the
   * monster there and opens the door, which is the whole point of it.
   *
   * The one rule that survives is the OCEAN RUSH rule: a sign may never sit on
   * a control. Instead of giving up when the short push out of a widget fails,
   * it now searches outward for a free slot, and only hides if the entire
   * canvas is somehow taken.
   */
  const EDGE_SLOP = 12

  const overlaps = (
    x: number,
    y: number,
    box: { l: number; t: number; r: number; b: number },
  ): boolean => !(x + HALF <= box.l || x - HALF >= box.r || y <= box.t || y - 88 >= box.b)

  /** Step out of `box` the short way. Vertical first — a sign already floats
   *  above its building, so moving it up or down keeps the association. */
  const push = (p: { x: number; y: number }, box: { l: number; t: number; r: number; b: number }): void => {
    const up = p.y - box.t
    const down = box.b - (p.y - 88)
    const left = p.x + HALF - box.l
    const right = box.r - (p.x - HALF)
    const min = Math.min(up, down, left, right)
    if (min === up) p.y -= up
    else if (min === down) p.y += down
    else if (min === left) p.x -= left
    else p.x += right
  }

  const project = (): void => {
    const show = visible() && signs.length > 0
    layer.dataset['on'] = String(show)
    if (!show) return
    // THE CARDS SHRINK WITH THE WORLD. One custom property on the layer, so the
    // browser resizes every card in one style recalculation rather than in one
    // per sign — and the 88px BUTTONS are untouched (see `chip()`).
    const zoom = Math.min(SIGN_SIZE.length - 1, Math.max(0, ctx.camera.zoomLevel))
    layer.style.setProperty('--sign-card', `${SIGN_SIZE[zoom] ?? 62}px`)
    const canvas = ctx.canvas.getBoundingClientRect()
    // `left`/`top` are resolved in the LAYER'S box, and the projection comes out
    // in the CANVAS'S box. They are usually the same rectangle and they are not
    // required to be — so convert, and clamp in the space the browser will
    // actually use. Getting this wrong put a sign 26px off the right-hand edge
    // in portrait while the clamp, computed in canvas space, thought it was
    // safely inside. Both boxes come from getBoundingClientRect; neither comes
    // from `window` (contract §12).
    const layerRect = layer.getBoundingClientRect()
    const dx = canvas.left - layerRect.left
    const dy = canvas.top - layerRect.top
    const maxX = layerRect.width
    const maxY = layerRect.height
    const boxes = chromeBoxes()
    /** Signs already placed this frame, so two signs never stack up either. */
    const placed: { l: number; t: number; r: number; b: number }[] = []

    /*
     * READ EVERYTHING FIRST, THEN WRITE. Measured, not assumed: this loop used
     * to project a sign (which reads a bounding rect) and then immediately
     * write its `left`/`top`, which invalidates layout — so the next sign's
     * read forced a full reflow. Five signs meant five reflows per pass, ten
     * passes a second, and on the 4x-throttled hand-me-down profile that alone
     * pushed per-frame CPU work from ~4ms to ~21ms and failed the perf gate at
     * p50. Splitting the reads from the writes costs one layout per pass.
     */
    const shots: ({ x: number; y: number } | null)[] = signs.map((sign) => {
      const screen = worldToCanvasPx(ctx.camera.camera, ctx.canvas, sign.at)
      return screen.behind ? null : { x: screen.x + dx, y: screen.y + dy - LIFT_PX }
    })
    const writes: (string | null)[] = []

    /** The rectangle a sign's anchor point may live in. Bottom-centre anchored. */
    const inL = HALF + 6
    const inR = Math.max(inL, maxX - HALF - 6)
    const inT = 94
    const inB = Math.max(inT, maxY - 6)
    const dock = (q: { x: number; y: number }): void => {
      q.x = Math.min(Math.max(q.x, inL), inR)
      q.y = Math.min(Math.max(q.y, inT), inB)
    }
    /*
     * WHERE THE TAPPABLE THINGS ARE, THIS FRAME. Projected once for the whole
     * pass — plain matrix maths, no layout reads. See `MAX_HOTSPOTS`.
     */
    const spots: { x: number; y: number; owner: string }[] = []
    for (const hs of hotspots) {
      const s = worldToCanvasPx(ctx.camera.camera, ctx.canvas, hs.at)
      if (s.behind) continue
      const x = s.x + dx
      const y = s.y + dy
      // Off the glass entirely? Then no sign can be sitting on it.
      if (x < -HALF || x > maxX + HALF || y < -88 || y > maxY + 88) continue
      spots.push({ x, y, owner: hs.owner })
    }
    /** Does the 88px box a sign would occupy here swallow somebody else's tap? */
    const eats = (x: number, y: number, owner: string | null): boolean =>
      spots.some(
        (s) =>
          s.owner !== owner &&
          s.x > x - HALF - HOTSPOT_PAD &&
          s.x < x + HALF + HOTSPOT_PAD &&
          s.y > y - 88 - HOTSPOT_PAD &&
          s.y < y + HOTSPOT_PAD,
      )
    const blocked = (x: number, y: number, owner: string | null): boolean =>
      boxes.some((b) => overlaps(x, y, b)) || placed.some((b) => overlaps(x, y, b)) || eats(x, y, owner)
    /** Eight ways out, nearest first, so a bumped sign moves the short way. */
    const WAYS: readonly (readonly [number, number])[] = [
      [0, -1],
      [0, 1],
      [-1, 0],
      [1, 0],
      [-1, -1],
      [1, -1],
      [-1, 1],
      [1, 1],
    ]

    for (let i = 0; i < signs.length; i++) {
      const want = shots[i]
      if (want === undefined || want === null) {
        writes.push(null)
        continue
      }
      const p = { ...want }
      const owner = signs[i]?.owner ?? null
      // Chrome, then the signs already standing, twice — enough for a corner
      // where two widgets meet.
      for (let pass = 0; pass < 2; pass++) {
        for (const box of boxes) if (overlaps(p.x, p.y, box)) push(p, box)
        for (const box of placed) if (overlaps(p.x, p.y, box)) push(p, box)
      }
      // DOCK LAST, and unconditionally. Everything above is best-effort
      // avoidance; this is the guarantee that a sign is never half off the
      // picture, whatever the projection and the pushes did. A sign whose
      // building is off screen ends up here, on the border, pointing out.
      dock(p)

      // Still overlapping something after that? Walk outward for a free slot
      // rather than deleting the only route a kid has to that door.
      if (blocked(p.x, p.y, owner)) {
        let found = false
        for (let ring = 1; ring <= 14 && !found; ring++) {
          for (const way of WAYS) {
            const q = { x: p.x + way[0] * ring * 26, y: p.y + way[1] * ring * 26 }
            dock(q)
            if (!blocked(q.x, q.y, owner)) {
              p.x = q.x
              p.y = q.y
              found = true
              break
            }
          }
        }
        // Every slot on the canvas taken (it never is): a sign on a control is
        // worse than no sign, so this one case still says nothing.
        if (!found) {
          writes.push(null)
          continue
        }
      }
      placed.push({
        l: p.x - HALF - SIGN_GUTTER,
        t: p.y - 88 - SIGN_GUTTER,
        r: p.x + HALF + SIGN_GUTTER,
        b: p.y + SIGN_GUTTER,
      })
      /*
       * "EDGE" MEANS THE BUILDING IS OFF THE PICTURE — not merely that the sign
       * had to shuffle round a button. Measuring it as raw drift marked signs
       * that were sitting squarely on their own shop as edge-docked and took
       * their posts away for no reason. It is the projection being outside the
       * frame that makes a post a lie, so that is what is tested.
       */
      const outside =
        want.x < inL - EDGE_SLOP ||
        want.x > inR + EDGE_SLOP ||
        want.y < inT - EDGE_SLOP ||
        want.y > inB + EDGE_SLOP
      writes.push(`${Math.round(p.x)}px|${Math.round(p.y)}px|${outside ? '1' : '0'}`)
    }

    // ...and now the writes, in one go.
    for (let i = 0; i < signs.length; i++) {
      const sign = signs[i]
      if (sign === undefined) continue
      const w = writes[i]
      if (w === undefined || w === null) {
        sign.node.style.display = 'none'
        continue
      }
      const [left, top, edge] = w.split('|')
      sign.node.style.display = 'flex'
      // CANVAS pixels, straight from the canvas rect. Never window.
      sign.node.style.left = left ?? '0px'
      sign.node.style.top = top ?? '0px'
      // Docked away from its building: drop the post, so it reads as "over
      // there" rather than as a label standing on the grass beneath it.
      sign.node.dataset['edge'] = edge ?? '0'
    }
  }

  bus.on('scene:entered', rebuild)
  bus.on('build:placed', rebuild)
  bus.on('build:removed', rebuild)
  bus.on('save:loaded', rebuild)
  bus.on('monster:lookChanged', rebuild)
  queueMicrotask(rebuild)

  /**
   * A SIGN ONLY MOVES WHEN THE CAMERA DOES. Signs are pinned to fixed points in
   * the world, so if the camera has not turned, zoomed or panned since the last
   * pass, every projection would come out identical — and projecting reads
   * bounding rects, which is the expensive half. These are plain property reads
   * with no layout cost, and they let a still screen cost nothing at all.
   */
  let lastCam = ''
  const camKey = (): string => {
    const t = ctx.camera.target
    return `${ctx.camera.rotation}|${ctx.camera.zoomLevel}|${Math.round(t.x * 4)}|${Math.round(t.z * 4)}|${signs.length}|${String(visible())}`
  }

  engine.addSystem({
    id: 'ui.signposts',
    order: 91,
    render: () => {
      tick = (tick + 1) % EVERY
      if (tick !== 0) return
      const key = camKey()
      if (key === lastCam) return
      lastCam = key
      project()
    },
  })
  // A rebuild changes the sign set without moving the camera.
  const invalidate = (): void => {
    lastCam = ''
  }
  bus.on('scene:entered', invalidate)
  bus.on('ui:panel', invalidate)
  bus.on('app:modeChanged', invalidate)
  // Turning the iPad moves every sign without moving the camera at all — the
  // one case the camera key cannot see.
  window.addEventListener('resize', invalidate)
  window.addEventListener('orientationchange', invalidate)
}
